import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { Strategies, Token, GasParamsStringType } from 'types/entities'

import { SupportedChainId } from 'constants/chains'
import { AirdropStatus } from 'constants/transaction'

import { StoredAirdropList, TxParams, Signature } from './types'

export interface State {
  readonly batchSize: string
  readonly token: Token | null
  readonly txsList: TxParams[]
  readonly strategy: Strategies
  readonly isDeflationary: boolean
  readonly airdropId: string | null
  readonly chainId: SupportedChainId
  readonly signature: Signature | null
  readonly airdropStatus: AirdropStatus
  readonly recipientsList: StoredAirdropList
  readonly exceptionsList: StoredAirdropList
  readonly gasParams: GasParamsStringType | null
}

const initialState: State = {
  token: null,
  recipientsList: [],

  txsList: [],
  batchSize: '0',
  airdropId: null,
  signature: null,
  exceptionsList: [],
  isDeflationary: false,

  gasParams: null,

  chainId: SupportedChainId.MAINNET,
  strategy: Strategies.EXTERNAL_WALLET,
  airdropStatus: AirdropStatus.NOT_STARTED,
}

const airdrop = createSlice({
  name: 'airdrop',
  initialState,
  reducers: {
    setRecipientsList: (state, action: PayloadAction<StoredAirdropList>) => {
      state.recipientsList = action.payload
    },
    setExceptionsList: (state, action: PayloadAction<StoredAirdropList>) => {
      state.exceptionsList = action.payload
    },
    setAirdropId: (state, action: PayloadAction<string>) => {
      state.airdropId = action.payload
    },
    setToken: (state, action: PayloadAction<Token | null>) => {
      state.token = action.payload
    },
    setSignature: (state, action: PayloadAction<Signature>) => {
      state.signature = action.payload
    },
    setAirdropChainId: (state, action: PayloadAction<SupportedChainId>) => {
      state.chainId = action.payload
    },
    setBatchSize: (state, action: PayloadAction<string>) => {
      state.batchSize = action.payload
    },
    setDeflationary: (state, action: PayloadAction<boolean>) => {
      state.isDeflationary = action.payload
    },
    setStrategy: (state, action: PayloadAction<Strategies>) => {
      state.strategy = action.payload
    },
    setTxsList: (state, action: PayloadAction<TxParams[]>) => {
      state.txsList = action.payload
    },
    setGasParams: (state, action: PayloadAction<GasParamsStringType>) => {
      state.gasParams = action.payload
    },
    setAirdropStatus: (state, action: PayloadAction<AirdropStatus>) => {
      state.airdropStatus = action.payload
    },
    // clear state
    clearPrepareAirdropData: (state) => {
      state.token = null
      state.signature = null
      state.recipientsList = []
    },
    clearAirdropParams: (state) => {
      return {
        ...initialState,
        signature: null,
        token: state.token,
        chainId: state.chainId,
        recipientsList: state.recipientsList,
        isDeflationary: state.isDeflationary,
      }
    },
    clearTokensList: () => initialState,
  },
})

export const {
  setToken,
  setTxsList,
  setStrategy,
  setGasParams,
  setSignature,
  setBatchSize,
  setAirdropId,
  setDeflationary,
  clearTokensList,
  setAirdropStatus,
  setExceptionsList,
  setRecipientsList,
  setAirdropChainId,
  clearAirdropParams,
  clearPrepareAirdropData,
} = airdrop.actions

export default airdrop.reducer
